import React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { FadeIn } from 'react-native-reanimated';

import { AvailabilityPicker } from '@/components/schedule/availability-picker';
import {
  ScheduleGradeState,
  WEEKDAYS_LONG,
  WEEKDAYS_SHORT,
  formatShortDate,
} from '@/components/schedule/use-schedule-grade';
import { ThemedText } from '@/components/themed-text';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';
import { AvailabilityDay } from '@/types/appointment';
import { formatCurrency } from '@/utils/format-currency';
import { formatSlotTime, zonedDateKey, zonedWeekday } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

const DEFAULT_BRAND = '#FD5001';

const BILLING_LABEL: Record<string, string> = {
  WEEKLY: 'semanal',
  MONTHLY: 'mensal',
  BIMONTHLY: 'bimestral',
  TRIMESTERLY: 'trimestral',
  SEMIANNUALLY: 'semestral',
  YEARLY: 'anual',
};

interface Props {
  grade: ScheduleGradeState;
  days: AvailabilityDay[];
  brand?: string | null;
  /** Mostra "N vagas" nos horários (serviços de turma). */
  showCapacity?: boolean;
  /** Linha de instrução acima dos controles. */
  hint?: string;
  /** Entre a agenda e o resumo — ex.: "quem está livre neste horário". */
  children?: React.ReactNode;
}

/** Segmento "só esse dia" × "toda &lt;dia&gt; &lt;hora&gt;". */
function RepeatSwitch({
  brand,
  repeat,
  repeatLabel,
  onChange,
}: {
  brand: string;
  repeat: boolean;
  repeatLabel: string;
  onChange: (repeat: boolean) => void;
}) {
  const th = useThemedHex();
  return (
    <View className="flex-row gap-2">
      {[
        { value: false, label: 'Só esse dia' },
        { value: true, label: repeatLabel },
      ].map((option) => {
        const active = repeat === option.value;
        return (
          <Pressable
            key={String(option.value)}
            onPress={() => onChange(option.value)}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            className="h-10 flex-1 items-center justify-center rounded-full active:scale-[0.98]"
            style={{ backgroundColor: active ? brand : th('#F5F5F5'), borderCurve: 'continuous' }}
          >
            <ThemedText
              numberOfLines={1}
              className="px-2 font-semibold text-[13px] capitalize"
              style={{ color: active ? '#fff' : th('#525252') }}
            >
              {option.label}
            </ThemedText>
          </Pressable>
        );
      })}
    </View>
  );
}

/**
 * A agenda, inteira: escolher, repetir, liberar uma data, trocar por outra e
 * ler o que vai ficar marcado. Uma só para o prestador (contrato) e para o
 * cliente (serviço logado ou público) — a diferença entre elas é só de termos,
 * que vêm no `grade`.
 */
export function ScheduleGrade({
  grade,
  days,
  brand: brandColor,
  showCapacity,
  hint,
  children,
}: Props) {
  const brand = brandColor || DEFAULT_BRAND;
  const {
    slots,
    extras,
    skips,
    repeat,
    setRepeat,
    toggleSlot,
    restoreSkip,
    selected,
    markedDates,
    highlightedStarts,
    plan,
    previewing,
    error,
    skipped,
    overQuota,
    count,
    sessionsPerRegime,
    maxRepeatSlots,
    quota,
    periodWord,
    isSubscription,
    canRepeat,
  } = grade;

  // O botão nomeia o que vai repetir ("Toda segunda 09:00"): sem o dia e a
  // hora no rótulo, "repetir toda semana" não dizia repetir O QUÊ.
  const first = slots[0];
  const repeatLabel =
    slots.length > 1
      ? `Toda semana · ${slots.length} dias`
      : first
        ? `Toda ${WEEKDAYS_LONG[zonedWeekday(first.startAt)]} ${formatSlotTime(first.startAt)}`
        : 'Repetir toda semana';

  const showControls = canRepeat && slots.length > 0;

  return (
    <View className="gap-4">
      {!!hint && (
        <ThemedText className="text-xs leading-5 text-neutral-500">{hint}</ThemedText>
      )}

      {showControls && (
        <AnimatedView entering={FadeIn.duration(220)} className="gap-3">
          <RepeatSwitch
            brand={brand}
            repeat={repeat}
            repeatLabel={repeatLabel}
            onChange={setRepeat}
          />

          {repeat && maxRepeatSlots != null && (
            <ThemedText className="text-xs leading-4 text-neutral-500">
              {maxRepeatSlots > 0
                ? `Repetindo toda semana, cabem ${maxRepeatSlots} ${
                    maxRepeatSlots > 1 ? 'horários' : 'horário'
                  } na cota de ${quota}/${periodWord}.`
                : `A cota de ${quota}/${periodWord} não cobre um horário toda semana. Deixe como datas soltas ou aumente a cota.`}
            </ThemedText>
          )}
        </AnimatedView>
      )}

      {/* Chips do que está montado: linhas, trocas e datas liberadas. */}
      {slots.length > 0 && (
        <View className="flex-row flex-wrap gap-2">
          {slots.map((slot) => (
            <PressableScale
              key={slot.startAt}
              haptic
              onPress={() => toggleSlot(slot)}
              accessibilityLabel={`Remover ${WEEKDAYS_SHORT[zonedWeekday(slot.startAt)]} ${formatSlotTime(slot.startAt)}`}
              className="h-8 flex-row items-center gap-1.5 rounded-full px-3"
              style={{ backgroundColor: `${brand}14` }}
            >
              <ThemedText
                className="font-semibold text-xs capitalize"
                style={{ color: brand }}
              >
                {WEEKDAYS_SHORT[zonedWeekday(slot.startAt)]}{' '}
                {repeat
                  ? formatSlotTime(slot.startAt)
                  : `${formatShortDate(zonedDateKey(slot.startAt))} · ${formatSlotTime(slot.startAt)}`}
              </ThemedText>
              <Ionicons name="close" size={13} color={brand} />
            </PressableScale>
          ))}
          {repeat &&
            extras.map((slot) => (
              <PressableScale
                key={`extra-${slot.startAt}`}
                haptic
                onPress={() => toggleSlot(slot)}
                className="h-8 flex-row items-center gap-1.5 rounded-full px-3"
                style={{ backgroundColor: `${brand}14` }}
              >
                <ThemedText className="font-semibold text-xs capitalize" style={{ color: brand }}>
                  {WEEKDAYS_SHORT[zonedWeekday(slot.startAt)]}{' '}
                  {formatShortDate(zonedDateKey(slot.startAt))} · {formatSlotTime(slot.startAt)}
                </ThemedText>
                <Ionicons name="close" size={13} color={brand} />
              </PressableScale>
            ))}
          {repeat &&
            skips.map((startAt) => (
              <PressableScale
                key={`skip-${startAt}`}
                haptic
                onPress={() => restoreSkip(startAt)}
                accessibilityLabel="Devolver esta data à repetição"
                className="h-8 flex-row items-center gap-1.5 rounded-full bg-neutral-100 px-3"
              >
                <ThemedText className="font-semibold text-xs capitalize text-neutral-400 line-through">
                  {WEEKDAYS_SHORT[zonedWeekday(startAt)]}{' '}
                  {formatShortDate(zonedDateKey(startAt))} · {formatSlotTime(startAt)}
                </ThemedText>
                <Ionicons className="text-neutral-400" name="arrow-undo" size={13} />
              </PressableScale>
            ))}
        </View>
      )}

      {repeat && slots.length > 0 && (
        <ThemedText className="text-xs leading-4 text-neutral-500">
          {skips.length > extras.length
            ? 'Data liberada. Toque em outro horário ou dia para colocar no lugar — ou deixe sem.'
            : 'Toque num dia que repete para liberar só aquela data. Outro horário da agenda vira mais um dia na semana.'}
        </ThemedText>
      )}

      <AvailabilityPicker
        days={days}
        brandColor={brand}
        selectedStart={selected?.startAt ?? null}
        onSelect={toggleSlot}
        showCapacity={showCapacity}
        markedDates={markedDates}
        highlightedStarts={highlightedStarts}
      />

      {children}

      {/* Resumo do que vai ficar marcado. */}
      {slots.length === 0 ? null : repeat ? (
        previewing ? (
          <View className="flex-row items-center gap-2">
            <ActivityIndicator size="small" color={brand} />
            <ThemedText className="text-xs text-neutral-400">Montando a agenda…</ThemedText>
          </View>
        ) : error ? (
          <ThemedText className="text-xs text-error-600">{error}</ThemedText>
        ) : plan ? (
          <View className="gap-1">
            <ThemedText
              className={`font-semibold text-[13px] ${
                overQuota ? 'text-error-600' : 'text-neutral-800'
              }`}
            >
              {quota
                ? `${sessionsPerRegime} ${
                    sessionsPerRegime === 1 ? 'sessão' : 'sessões'
                  } por ${periodWord}`
                : `${plan.totalSessions} ${
                    plan.totalSessions === 1 ? 'sessão' : 'sessões'
                  } até ${formatShortDate(plan.endDate)}`}
            </ThemedText>
            {plan.price != null && !quota && (
              <ThemedText className="text-xs text-neutral-500">
                Cobrança {BILLING_LABEL[plan.billingInterval] ?? 'recorrente'} de{' '}
                {formatCurrency(plan.price)}.
              </ThemedText>
            )}
            {skipped.length > 0 && (
              <View className="gap-0.5">
                <ThemedText className="text-xs text-amber-600">
                  {skipped.length}{' '}
                  {skipped.length === 1 ? 'data fica de fora' : 'datas ficam de fora'}:
                </ThemedText>
                {skipped.slice(0, 4).map((s) => (
                  <ThemedText key={s.key} className="text-xs text-amber-600">
                    • {s.when} — {s.label}
                  </ThemedText>
                ))}
                {skipped.length > 4 && (
                  <ThemedText className="text-xs text-amber-600">
                    • +{skipped.length - 4} {skipped.length - 4 === 1 ? 'outra' : 'outras'}
                  </ThemedText>
                )}
              </View>
            )}
            {overQuota && (
              <ThemedText className="text-xs text-error-600">
                Passa das {quota} sessões/{periodWord} da assinatura. Tire um horário ou aumente a
                cota.
              </ThemedText>
            )}
            {plan.issues.map((issue, index) => (
              <ThemedText key={index} className="text-xs text-error-600">
                {issue}
              </ThemedText>
            ))}
          </View>
        ) : null
      ) : isSubscription ? (
        <ThemedText className="font-semibold text-xs leading-4 text-neutral-800">
          {count} {count === 1 ? 'sessão pré-marcada' : 'sessões pré-marcadas'} nas datas escolhidas.
          {quota ? ` O resto da cota de ${quota}/${periodWord} fica para marcar depois.` : ''}
        </ThemedText>
      ) : null}
    </View>
  );
}
